import React, {
  Dispatch,
  SetStateAction,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  CircularProgress,
  InputAdornment,
  GlobalStyles,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getToken } from "../../../utils/utils";
import { getTaskSummFilterOptions } from "../api"; // ⬅️ use the Summary API helper
import arrowLeft from "../../../assests/images/arrow-left.svg";
import arrowRight from "../../../assests/images/arrow-right.png";
import doubleArrowLeft from "../../../assests/images/double-arrow-left.svg";
import doubleArrowRight from "../../../assests/images/double-arrow-right.svg";

const CLEAR_X_DATA_URI =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="#888" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59L7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/></svg>`
  );

const CustomArrow = ({ open, onClick }: { open?: boolean; onClick?: () => void }) => (
  <span
    onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRight: "2px solid #222",
      borderBottom: "2px solid #222",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transformOrigin: "center center",
      transition: "transform 0.18s ease",
      marginLeft: 12,
      position: "relative",
      cursor: "pointer",
      boxSizing: "border-box",
    }}
  />
);

/** ========= Backend payload (SUMMARY) ========= */
export interface TaskSummFilterPayload {
  outlet_name?: string[] | null;      // accounts.name
  pmi_entity_code?: string[] | null;  // accounts.dte_account_id
  task_type_en?: string[] | null;     // tasks.task_type_en
  task_start_from?: string | null;    // yyyy-MM-dd
  task_start_to?: string | null;      // yyyy-MM-dd
  task_end_from?: string | null;      // yyyy-MM-dd
  task_end_to?: string | null;        // yyyy-MM-dd
  search_key?: string | null;         // outlet/mobile/user (optional)
  page?: number;
  limit?: number;
}

interface Props {
  isOpen?: boolean;
  shouldFetch?: boolean;
  onFetched?: () => void;
  onApplyFilters: (payload: TaskSummFilterPayload) => void;
  onCancel?: () => void;

  // (Optional) expose date state to a parent if you want
  dateRange?: {
    start?: Date | null;
    end?: Date | null;
  };
  setDateRange?: Dispatch<SetStateAction<{ start?: Date | null; end?: Date | null }>>;

  // pre-load existing filters (rehydrate)
  initialFilters?: Partial<TaskSummFilterPayload> | null;
}

/** ========= MUI input wrapper for DatePicker ========= */
const ForwardedTextInput = forwardRef<HTMLInputElement, any>(
  ({ value, onClick, placeholder, style, sx, ...rest }, ref) => {
    const mergedStyle = { paddingRight: 40, ...(style || {}), width: "100%" };
    return (
      <TextField
        size="small"
        variant="outlined"
        inputRef={ref}
        onClick={onClick}
        value={value}
        placeholder={placeholder || "Select date range"}
        fullWidth
        {...rest}
        sx={{ position: "relative", width: "100%", ...(sx || {}) }}
        InputProps={{
          ...(rest.InputProps || {}),
          endAdornment: (
            <>
              {rest.InputProps?.endAdornment}
              <InputAdornment position="end" sx={{ position: "absolute", right: 10 }}>
                <CustomArrow
                  open={false}
                  onClick={() => {
                    try { onClick && (onClick as unknown as () => void)(); } catch { onClick?.(); }
                  }}
                />
              </InputAdornment>
            </>
          ),
        }}
        inputProps={{
          ...(rest.inputProps || {}),
          style: mergedStyle,
        }}
      />
    );
  }
);
ForwardedTextInput.displayName = "ForwardedTextInput";

/** ========= Compact multiselect with search (same UX as your Trans popup) ========= */
function CustomMultiSelectWithSearch({
  label,
  options,
  value,
  onChange,
  loading,
  formatOptionLabel,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (vals: string[]) => void;
  loading?: boolean;
  formatOptionLabel?: (s: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const fmt = (s: string) => (formatOptionLabel ? formatOptionLabel(s) : s);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearchValue("");
      }
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && (setOpen(false), setSearchValue(""));
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const filtered = (options || []).filter((opt) =>
    fmt(opt).toLowerCase().includes(searchValue.toLowerCase())
  );

  const allSelected = value.length > 0 && value.length === options.length;
  const displayedValue =
    value.length === 0
      ? label
      : value.length <= 2
      ? value.map(fmt).join(", ")
      : `${value.slice(0, 2).map(fmt).join(", ")}…`;

  const toggleOption = (opt: string) =>
    value.includes(opt) ? onChange(value.filter((v) => v !== opt)) : onChange([...value, opt]);

  const toggleSelectAll = () => (allSelected ? onChange([]) : onChange(options.slice()));

  return (
    <Box ref={containerRef} sx={{ width: "100%", position: "relative", userSelect: "none" }}>
      <TextField
        size="small"
        variant="outlined"
        value={displayedValue}
        onClick={() => setOpen((v) => !v)}
        placeholder={label}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <>
              {loading ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
              <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                <CustomArrow open={open} onClick={() => setOpen((v) => !v)} />
              </InputAdornment>
            </>
          ),
          sx: {
            cursor: "pointer",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
        fullWidth
      />

      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxHeight: 250,
            overflowY: "auto",
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            zIndex: 3500,
            mt: 0.5,
            borderRadius: 1,
            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ px: 1, py: 0.5 }}>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search..."
              style={{
                width: "100%",
                padding: "10px 10px",
                borderRadius: 4,
                border: "1px solid #ddd",
                fontSize: "0.9rem",
                marginBottom: 8,
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: "5px",
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Box>

          <Box sx={{ px: 1 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", mb: 1, cursor: "pointer" }}
              onClick={toggleSelectAll}
            >
              <Checkbox checked={allSelected} indeterminate={value.length > 0 && !allSelected} />
              <Typography variant="body2">Select All</Typography>
            </Box>

            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
                No options found
              </Typography>
            )}

            {filtered.map((opt) => {
              const checked = value.includes(opt);
              return (
                <Box
                  key={opt}
                  sx={{ display: "flex", alignItems: "center", py: 0.5, cursor: "pointer" }}
                  onClick={() => toggleOption(opt)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Checkbox checked={checked} />
                  <Typography variant="body2">{fmt(opt)}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

/** ========= Popup ========= */
export default function TaskSummFilterPopup({
  isOpen = false,
  shouldFetch = false,
  onFetched,
  onApplyFilters,
  onCancel,
  dateRange,
  setDateRange,
  initialFilters,
}: Props) {
  const token = getToken();

  // prevent body scroll when open
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prev = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      overflow: document.body.style.overflow,
    };
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = prev.position || "";
      document.body.style.top = prev.top || "";
      document.body.style.left = prev.left || "";
      document.body.style.right = prev.right || "";
      document.body.style.overflow = prev.overflow || "";
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // ====== Options ======
  const [outletNameOptions, setOutletNameOptions] = useState<string[]>([]);
  const [pmiOptions, setPmiOptions] = useState<string[]>([]);
  const [taskTypeOptions, setTaskTypeOptions] = useState<string[]>([]);

  // ====== Loading flags ======
  const [loadingOutletNames, setLoadingOutletNames] = useState(false);
  const [loadingPmi, setLoadingPmi] = useState(false);
  const [loadingTaskTypes, setLoadingTaskTypes] = useState(false);

  // ====== Selections ======
  const [selectedOutletNames, setSelectedOutletNames] = useState<string[]>([]);
  const [selectedPmiEntities, setSelectedPmiEntities] = useState<string[]>([]);
  const [selectedTaskTypes, setSelectedTaskTypes] = useState<string[]>([]);
  const [searchKey, setSearchKey] = useState<string>("");

  // Task date range (maps to task start/end on BE)
  const [taskWindow, setTaskWindow] = useState<[Date | null, Date | null]>([
    dateRange?.start ?? null,
    dateRange?.end ?? null,
  ]);

  // Abort + guards
  const filterAbortRef = useRef<AbortController | null>(null);
  const onFetchedCalledRef = useRef(false);
  const userInteractedRef = useRef(false);
  const markInteraction = () => { userInteractedRef.current = true; };

  const sameArray = (a: string[], b: string[]) => {
    if (a === b) return true;
    if (!a || !b || a.length !== b.length) return false;
    const as = [...a].sort();
    const bs = [...b].sort();
    for (let i = 0; i < as.length; i++) if (as[i] !== bs[i]) return false;
    return true;
  };

  const toYMD = (d: Date | null): string | null => {
    if (!d) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // wrapped setters (flag interaction)
  const handleSetSelectedOutletNames = (v: string[]) => { markInteraction(); setSelectedOutletNames(v); };
  const handleSetSelectedPmiEntities = (v: string[]) => { markInteraction(); setSelectedPmiEntities(v); };
  const handleSetSelectedTaskTypes = (v: string[]) => { markInteraction(); setSelectedTaskTypes(v); };
  const handleSetTaskWindow = (r: [Date | null, Date | null]) => { markInteraction(); setTaskWindow(r); };

  const safeArray = (v: any): string[] => (Array.isArray(v) ? v.filter(Boolean).map(String) : []);

  /** Fetch narrowed options from BE (Summary) */
  const fetchFilterOptions = async () => {
    if (filterAbortRef.current) {
      try { filterAbortRef.current.abort(); } catch {}
    }
    const ctrl = new AbortController();
    filterAbortRef.current = ctrl;

    try {
      setLoadingOutletNames(true);
      setLoadingPmi(true);
      setLoadingTaskTypes(true);

      const fo = await getTaskSummFilterOptions(
        {
          outlet_name: selectedOutletNames.length ? selectedOutletNames : null,
          pmi_entity_code: selectedPmiEntities.length ? selectedPmiEntities : null,
          task_type_en: selectedTaskTypes.length ? selectedTaskTypes : null,
          task_start_from: toYMD(taskWindow[0]),
          task_end_to: toYMD(taskWindow[1]),
          search_key: searchKey || null,
        },
        token!,
        ctrl.signal
      );
      if (ctrl.signal.aborted) return;

      const names = safeArray((fo as any)?.available_outlet_names);
      const pmi = safeArray((fo as any)?.available_pmi_codes);
      const types = safeArray((fo as any)?.available_task_types);

      setOutletNameOptions(names);
      setPmiOptions(pmi);
      setTaskTypeOptions(types);

      // prune selections if now invalid
      setSelectedOutletNames((prev) => {
        const next = prev.filter((p) => names.includes(p));
        return sameArray(next, prev) ? prev : next;
      });
      setSelectedPmiEntities((prev) => {
        const next = prev.filter((p) => pmi.includes(p));
        return sameArray(next, prev) ? prev : next;
      });
      setSelectedTaskTypes((prev) => {
        const next = prev.filter((p) => types.includes(p));
        return sameArray(next, prev) ? prev : next;
      });
    } catch (err: any) {
      if (err?.name !== "AbortError") console.error("Task Summ filter options failed:", err);
    } finally {
      if (filterAbortRef.current === ctrl) filterAbortRef.current = null;
      setLoadingOutletNames(false);
      setLoadingPmi(false);
      setLoadingTaskTypes(false);
    }
  };

  // initial load on open
  useEffect(() => {
    if (!isOpen || !shouldFetch) return;
    let cancelled = false;

    (async () => {
      try {
        await fetchFilterOptions();
        if (!cancelled && !onFetchedCalledRef.current) {
          onFetchedCalledRef.current = true;
          onFetched?.();
        }
      } catch (e) {
        console.error(e);
      }
    })();

    return () => {
      cancelled = true;
      if (filterAbortRef.current) {
        try { filterAbortRef.current.abort(); } catch {}
        filterAbortRef.current = null;
      }
      onFetchedCalledRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, shouldFetch]);

  // rehydrate from initialFilters
  useEffect(() => {
    if (!initialFilters) return;
    setSelectedOutletNames(initialFilters.outlet_name ?? []);
    setSelectedPmiEntities(initialFilters.pmi_entity_code ?? []);
    setSelectedTaskTypes(initialFilters.task_type_en ?? []);
    setSearchKey(initialFilters.search_key ?? "");

    const fromS = initialFilters.task_start_from ?? null;
    const toE = initialFilters.task_end_to ?? null;
    const from = fromS ? new Date(fromS) : null;
    const to = toE ? new Date(toE) : null;
    setTaskWindow([from, to]);
  }, [initialFilters]);

  // auto-refetch on user interaction
  useEffect(() => {
    if (!userInteractedRef.current) return;
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOutletNames, selectedPmiEntities, selectedTaskTypes, taskWindow, searchKey]);

  // Header with month/year arrows
  const btnResetStyle: React.CSSProperties = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const renderHeaderWithIcons = ({
    date,
    decreaseMonth,
    increaseMonth,
    decreaseYear,
    increaseYear,
  }: any) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <button type="button" onClick={decreaseYear} aria-label="Previous year" style={btnResetStyle}>
        <img src={doubleArrowLeft} alt="Previous year" />
      </button>
      <button type="button" onClick={decreaseMonth} aria-label="Previous month" style={btnResetStyle}>
        <img src={arrowLeft} alt="Previous month" />
      </button>

      <span style={{ margin: "0 10px", fontWeight: 600 }}>
        {date.toLocaleString("default", { year: "numeric", month: "long" })}
      </span>

      <button type="button" onClick={increaseMonth} aria-label="Next month" style={btnResetStyle}>
        <img src={arrowRight} alt="Next month" />
      </button>
      <button type="button" onClick={increaseYear} aria-label="Next year" style={btnResetStyle}>
        <img src={doubleArrowRight} alt="Next year" />
      </button>
    </div>
  );

  const formatRangeText = (range: [Date | null, Date | null]) => {
    if (!range || (!range[0] && !range[1])) return "Select date range";
    const s = range[0] ? range[0].toLocaleDateString() : "";
    const e = range[1] ? range[1].toLocaleDateString() : "";
    return s && e ? `${s} - ${e}` : s || e || "Select date range";
  };

  const clearAll = () => {
    userInteractedRef.current = false;

    setSelectedOutletNames([]);
    setSelectedPmiEntities([]);
    setSelectedTaskTypes([]);
    setSearchKey("");
    setTaskWindow([null, null]);
    setDateRange?.({ start: null, end: null });

    onApplyFilters({
      outlet_name: null,
      pmi_entity_code: null,
      task_type_en: null,
      task_start_from: null,
      task_end_to: null,
      search_key: null,
    });

    onCancel?.();
  };

  const handleApply = () => {
    setDateRange?.({ start: taskWindow[0], end: taskWindow[1] });
    onApplyFilters({
      outlet_name: selectedOutletNames.length ? selectedOutletNames.slice() : null,
      pmi_entity_code: selectedPmiEntities.length ? selectedPmiEntities.slice() : null,
      task_type_en: selectedTaskTypes.length ? selectedTaskTypes.slice() : null,
      task_start_from: toYMD(taskWindow[0]),
      task_end_to: toYMD(taskWindow[1]),
      search_key: searchKey ? searchKey.trim() : null,
    });
  };

  return (
    <Box
      sx={{
        width: 460,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "80vh",
        background: "#fff",
        borderRadius: 2,
        p: 0,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ p: 2.5, borderBottom: "1px solid #eee" }}>
        <Typography sx={{ fontWeight: 600 }}>Apply Filters</Typography>
      </Box>

      <GlobalStyles styles={{ ".react-datepicker-wrapper": { width: "100% !important" } }} />

      <Box sx={{ flex: 1, overflowY: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Search (optional) */}
        {/* <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Search (Outlet / Mobile / User)</Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Type to search…"
            value={searchKey}
            onChange={(e) => { markInteraction(); setSearchKey(e.target.value); }}
          />
        </Box> */}

        {/* PMI */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select PMI Entity</Typography>
          <CustomMultiSelectWithSearch
            label="Select PMI Entity"
            options={pmiOptions}
            value={selectedPmiEntities}
            onChange={handleSetSelectedPmiEntities}
            loading={loadingPmi}
          />
        </Box>

        {/* Outlet Names */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Outlet Names</Typography>
          <CustomMultiSelectWithSearch
            label="Select Outlet Names"
            options={outletNameOptions}
            value={selectedOutletNames}
            onChange={handleSetSelectedOutletNames}
            loading={loadingOutletNames}
          />
        </Box>

        {/* Task Types */}
        {/* <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Task Type</Typography>
          <CustomMultiSelectWithSearch
            label="Select Task Type"
            options={taskTypeOptions}
            value={selectedTaskTypes}
            onChange={handleSetSelectedTaskTypes}
            loading={loadingTaskTypes}
          />
        </Box> */}

        {/* Task Date Window */}
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Task Date </Typography>
          <Box sx={{ width: "100%" }}>
            <DatePicker
              selectsRange
              startDate={taskWindow[0] ?? undefined}
              endDate={taskWindow[1] ?? undefined}
              onChange={(range: [Date | null, Date | null]) => handleSetTaskWindow(range)}
              dateFormat="yyyy-MM-dd"
              renderCustomHeader={renderHeaderWithIcons}
              customInput={
                <ForwardedTextInput
                  placeholder={formatRangeText(taskWindow)}
                  key={`task-${taskWindow[0]?.getTime() ?? 0}-${taskWindow[1]?.getTime() ?? 0}`}
                  InputProps={{
                    endAdornment:
                      taskWindow[0] || taskWindow[1] ? (
                        <img
                          onClick={(e) => { e.stopPropagation(); handleSetTaskWindow([null, null]); }}
                          src={CLEAR_X_DATA_URI}
                          alt="Clear"
                          style={{ height: 18, width: 18, cursor: "pointer", marginRight: 10, paddingTop: "4px" }}
                        />
                      ) : undefined,
                  }}
                />
              }
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 2.5, borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 1.25 }}>
        <Button onClick={clearAll} sx={{ border: "1px solid #E7E8E9", color: "#000", borderRadius: 2, height: 40 }}>
          CLEAR ALL
        </Button>
        <Button onClick={handleApply} sx={{ border: "1px solid #C8172F", color: "#C8172F", borderRadius: 2, height: 40 }}>
          APPLY
        </Button>
      </Box>
    </Box>
  );
}
